import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CurrencyDisplay from '../components/CurrencyDisplay';
import useElementToPdf from '../utils/useElementToPdf';
import {
  Users, TrendingUp, TrendingDown, Repeat, UserPlus, UserMinus,
  AlertTriangle, Crown, Sparkles, Printer, RefreshCw, Calendar, Star,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from 'recharts';

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const part = new Intl.DateTimeFormat('en-CA', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Dubai',
    }).format(d);
    const [y, m, dd] = part.split('-');
    return `${dd}/${m}/${y}`;
  } catch { return '—'; }
};

const DeltaBadge = ({ value, suffix = '%', invert = false }) => {
  const v = Number(value || 0);
  const positive = invert ? v < 0 : v > 0;
  const negative = invert ? v > 0 : v < 0;
  const color = v === 0 ? 'text-muted-foreground' : positive ? 'text-emerald-400' : 'text-red-400';
  const Icon = v === 0 ? Repeat : positive ? TrendingUp : TrendingDown;
  const sign = v > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${color}`}>
      <Icon className="w-3 h-3" />
      {sign}{v}{suffix}
    </span>
  );
};

const Kpi = ({ label, value, delta, deltaSuffix = '%', icon: Icon, accent = 'text-primary', testid }) => (
  <div className="bg-card border border-white/10 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${accent}`} />}
    </div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    {delta !== undefined && (
      <div className="mt-1"><DeltaBadge value={delta} suffix={deltaSuffix} /></div>
    )}
  </div>
);

const CustomerInsights = () => {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [anchorDate, setAnchorDate] = useState(new Date().toISOString().slice(0, 10));
  const [includeAi, setIncludeAi] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `KE_CustomerInsights_${period}_${anchorDate}`,
    pageStyle: '@page { size: A4 portrait; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/insights/customer-retention', {
        params: { period, date: anchorDate, include_ai: includeAi },
      });
      setData(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'فشل تحميل التقرير');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [period, anchorDate]);

  const healthColor = useMemo(() => {
    const h = Number(data?.health_score || 0);
    if (h >= 75) return 'text-emerald-400';
    if (h >= 50) return 'text-amber-400';
    return 'text-red-400';
  }, [data]);

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-insights-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
            <Sparkles className="w-7 h-7" />
            ذكاء الزبائن
          </h1>
          <p className="text-muted-foreground text-sm">
            هل العملاء يزدادون أم يقلّون؟ ما الفرق عن الأسبوع/الشهر السابق؟ ومن المعرّضون للفقد؟
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-secondary/40 rounded-lg p-1">
            <Button
              size="sm" variant={period === 'weekly' ? 'default' : 'ghost'}
              onClick={() => setPeriod('weekly')}
              className={period === 'weekly' ? 'bg-primary text-primary-foreground' : ''}
              data-testid="insights-weekly-btn"
            >أسبوعي</Button>
            <Button
              size="sm" variant={period === 'monthly' ? 'default' : 'ghost'}
              onClick={() => setPeriod('monthly')}
              className={period === 'monthly' ? 'bg-primary text-primary-foreground' : ''}
              data-testid="insights-monthly-btn"
            >شهري</Button>
          </div>
          <Input
            type="date"
            value={anchorDate}
            onChange={(e) => setAnchorDate(e.target.value)}
            className="bg-secondary/40 border-white/10 w-44"
            data-testid="insights-date-input"
          />
          <Button
            variant="outline"
            className="border-white/10 gap-2"
            onClick={fetchData}
            disabled={loading}
            data-testid="insights-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button
            variant="outline"
            className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 gap-2"
            disabled={!data || loading}
            onClick={handlePrint}
            data-testid="insights-print-btn"
          >
            <Printer className="w-4 h-4" />
            تنزيل PDF
          </Button>
        </div>
      </div>

      {/* Printable area */}
      <div ref={printRef} className="space-y-6 bg-background text-foreground p-2 rounded">
        {loading || !data ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Window summary */}
            <div className="bg-card border border-primary/30 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                الفترة الحالية: <strong>{fmtDate(data.current_window.start)} → {fmtDate(data.current_window.end_exclusive)}</strong>
                <span className="text-muted-foreground/60">•</span>
                السابقة: <strong>{fmtDate(data.previous_window.start)} → {fmtDate(data.previous_window.end_exclusive)}</strong>
              </div>
              <div className="flex items-center gap-2" data-testid="insights-health-score">
                <span className="text-xs text-muted-foreground">صحة الصالون:</span>
                <span className={`text-2xl font-bold ${healthColor}`}>{data.health_score}<span className="text-xs">/100</span></span>
              </div>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <Kpi label="عملاء فريدون" value={fmtNum(data.current.unique_customers)} delta={data.deltas.unique_customers} icon={Users} accent="text-primary" testid="kpi-unique" />
              <Kpi label="جدد" value={fmtNum(data.current.new_customers)} delta={data.deltas.new_customers} icon={UserPlus} accent="text-emerald-400" testid="kpi-new" />
              <Kpi label="عائدون" value={fmtNum(data.current.returning_customers)} delta={data.deltas.returning_customers} icon={Repeat} accent="text-blue-400" testid="kpi-returning" />
              <Kpi label="مفقودون" value={fmtNum(data.lost_customers_count)} icon={UserMinus} accent="text-red-400" testid="kpi-lost" />
              <Kpi label="إجمالي الزيارات" value={fmtNum(data.current.total_visits)} delta={data.deltas.total_visits} icon={Calendar} accent="text-amber-400" testid="kpi-visits" />
              <Kpi label="معدل الاحتفاظ" value={`${data.current.retention_rate}%`} delta={data.deltas.retention_rate} deltaSuffix="نقطة" icon={Star} accent="text-purple-400" testid="kpi-retention" />
              <Kpi label="إجمالي الإيراد" value={<CurrencyDisplay amount={data.current.total_revenue} className="text-primary" />} delta={data.deltas.total_revenue} icon={TrendingUp} accent="text-primary" testid="kpi-revenue" />
            </div>

            {/* Trend chart */}
            <div className="bg-card border border-white/10 rounded-xl p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2 text-primary">
                <TrendingUp className="w-4 h-4" />
                تطور العملاء والإيرادات — آخر 8 {period === 'weekly' ? 'أسابيع' : 'أشهر'}
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.trend} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="custGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" stroke="#888" fontSize={11} />
                    <YAxis stroke="#888" fontSize={11} yAxisId="left" />
                    <YAxis stroke="#D4AF37" fontSize={11} yAxisId="right" orientation="right" />
                    <Tooltip contentStyle={{ background: '#1c1c1c', border: '1px solid rgba(255,255,255,0.08)' }} />
                    <Legend />
                    <Area type="monotone" dataKey="customers" name="العملاء" stroke="#10b981" fillOpacity={1} fill="url(#custGrad)" yAxisId="left" />
                    <Area type="monotone" dataKey="revenue" name="الإيراد" stroke="#D4AF37" fillOpacity={1} fill="url(#revGrad)" yAxisId="right" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Per-barber */}
            <div className="bg-card border border-white/10 rounded-xl p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2 text-primary">
                <Users className="w-4 h-4" />
                أداء كل حلاق
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground border-b border-white/10">
                    <tr>
                      <th className="text-start p-2">الحلاق</th>
                      <th className="text-center p-2">عملاء فريدون</th>
                      <th className="text-center p-2">جدد</th>
                      <th className="text-center p-2">عائدون</th>
                      <th className="text-center p-2">معدل الاحتفاظ</th>
                      <th className="text-center p-2">متوسط زيارات/عميل</th>
                      <th className="text-end p-2">الإيراد</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.current.per_barber.length === 0 ? (
                      <tr><td colSpan="7" className="text-center text-muted-foreground py-4">لا يوجد بيانات</td></tr>
                    ) : data.current.per_barber.map((b) => (
                      <tr key={b.barber_id} className="border-b border-white/5">
                        <td className="p-2 font-medium">{b.barber_name}</td>
                        <td className="p-2 text-center">{b.unique_customers}</td>
                        <td className="p-2 text-center text-emerald-400">{b.new_customers}</td>
                        <td className="p-2 text-center text-blue-400">{b.returning_customers}</td>
                        <td className="p-2 text-center">{b.retention_rate}%</td>
                        <td className="p-2 text-center">{b.avg_visits_per_customer}</td>
                        <td className="p-2 text-end font-mono"><CurrencyDisplay amount={b.revenue} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* At-risk customers */}
              <div className="bg-card border border-red-500/30 rounded-xl p-4" data-testid="at-risk-section">
                <h3 className="font-bold mb-3 flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  معرّضون للفقد ({data.at_risk.length})
                </h3>
                <p className="text-xs text-muted-foreground mb-3">عملاء كانوا منتظمين ولم يزوروا منذ 30–90 يوماً.</p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.at_risk.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">لا يوجد عملاء في خطر — أداء ممتاز!</p>
                  ) : data.at_risk.map((c) => (
                    <div key={c.membership_number} className="flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-lg p-2 text-sm">
                      <div className="flex-1">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">#{c.membership_number} • {c.phone || '—'}</div>
                      </div>
                      <div className="text-end text-xs">
                        <div className="text-red-400 font-bold">منذ {c.days_since_visit} يوم</div>
                        <div className="text-muted-foreground">{c.total_visits} زيارة سابقة</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top loyal */}
              <div className="bg-card border border-amber-500/30 rounded-xl p-4" data-testid="top-loyal-section">
                <h3 className="font-bold mb-3 flex items-center gap-2 text-amber-400">
                  <Crown className="w-4 h-4" />
                  أكثر العملاء وفاء (مدى الحياة)
                </h3>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.top_loyal.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">لا يوجد بيانات بعد.</p>
                  ) : data.top_loyal.map((c, idx) => (
                    <div key={c.membership_number} className="flex items-center justify-between bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">#{c.membership_number} • {c.visits} زيارة</div>
                        </div>
                      </div>
                      <div className="text-end font-mono text-amber-400 font-bold text-sm">
                        <CurrencyDisplay amount={c.revenue} className="text-amber-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Suggestions / AI summary */}
            <div className="bg-card border border-emerald-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold flex items-center gap-2 text-emerald-400">
                  <Sparkles className="w-4 h-4" />
                  توصيات لرفع ولاء العملاء
                </h3>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAi}
                    onChange={(e) => { setIncludeAi(e.target.checked); }}
                    className="accent-emerald-500"
                    data-testid="insights-ai-toggle"
                  />
                  تفعيل تحليل الذكاء الاصطناعي
                </label>
              </div>
              <ul className="space-y-2 text-sm">
                {data.suggestions.map((s, idx) => (
                  <li key={idx} className="bg-emerald-500/5 border-r-2 border-emerald-500 rounded-lg p-2 text-emerald-100/90">
                    {s}
                  </li>
                ))}
              </ul>
              {data.ai_summary && (
                <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg" data-testid="insights-ai-summary">
                  <div className="text-xs text-purple-300 font-bold mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> تحليل الذكاء الاصطناعي
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-purple-100/90">{data.ai_summary}</div>
                </div>
              )}
              {includeAi && !data.ai_summary && (
                <Button
                  size="sm"
                  className="mt-3 bg-purple-500 hover:bg-purple-600 gap-2"
                  onClick={fetchData}
                  data-testid="insights-generate-ai-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  توليد التحليل الذكي
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerInsights;
